import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { TutorVersion } from "@/services/api.service";

import { PromptTextDiff } from "./PromptTextDiff";
import { TvIdPill } from "./TvIdPill";
import {
  diffTutorVersions,
  formatValue,
  type PromptChange,
  type PromptChangeKind,
} from "./tvDiff";

const KIND_STYLE: Record<PromptChangeKind, { label: string; className: string }> = {
  repinned: { label: "repinned", className: "text-primary" },
  modified: { label: "modified", className: "text-primary" },
  added: { label: "added", className: "text-primary" },
  removed: { label: "removed", className: "text-destructive" },
};

interface TutorVersionDiffProps {
  versions: TutorVersion[];
  fromId?: string;
  toId?: string;
  /** Shown above the diff; the pair is driven by the caller's selection. */
  caption?: React.ReactNode;
}

export const TutorVersionDiff = ({
  versions,
  fromId,
  toId,
  caption,
}: TutorVersionDiffProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const from = versions.find((v) => v.tutor_version_id === fromId);
  const to = versions.find((v) => v.tutor_version_id === toId);
  const diff = useMemo(
    () => (from && to ? diffTutorVersions(from, to) : null),
    [from, to],
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <div className="space-y-3">
      {caption}

      {!from || !to ? (
        <Card className="p-8 text-center text-muted-foreground">
          Nothing to compare.
        </Card>
      ) : diff?.isEmpty ? (
        <Card className="flex flex-wrap items-center justify-center gap-1.5 p-8 text-center text-muted-foreground">
          No differences between <TvIdPill id={from.tutor_version_id} /> and{" "}
          <TvIdPill id={to.tutor_version_id} />.
        </Card>
      ) : (
        diff && (
          <div className="space-y-3">
            {/* Prompts */}
            <Card className="space-y-2 p-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Prompts</h4>
                <span className="text-xs text-muted-foreground">
                  {diff.prompts.length} changed · {diff.unchangedPromptCount} unchanged
                </span>
              </div>

              {diff.prompts.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No prompt pins changed.
                </p>
              ) : (
                <div className="space-y-1">
                  {diff.prompts.map((c: PromptChange) => {
                    const canExpand =
                      c.kind === "repinned" &&
                      c.fromVersion !== undefined &&
                      c.toVersion !== undefined;
                    const isOpen = expanded.has(c.prompt_id);
                    return (
                      <div key={c.prompt_id} className="rounded border">
                        <button
                          type="button"
                          disabled={!canExpand}
                          onClick={() => canExpand && toggle(c.prompt_id)}
                          className={cn(
                            "flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm",
                            canExpand && "hover:bg-muted/50",
                          )}
                        >
                          {canExpand ? (
                            isOpen ? (
                              <ChevronDown className="size-3.5 shrink-0" />
                            ) : (
                              <ChevronRight className="size-3.5 shrink-0" />
                            )
                          ) : (
                            <span className="w-3.5 shrink-0" />
                          )}
                          <code className="truncate font-mono text-xs">
                            {c.prompt_id}
                          </code>
                          <span
                            className={cn(
                              "ml-auto shrink-0 text-xs",
                              KIND_STYLE[c.kind].className,
                            )}
                          >
                            {c.kind === "repinned" || c.kind === "modified" ? (
                              <span className="font-mono">
                                v{c.fromVersion} → v{c.toVersion}
                              </span>
                            ) : c.kind === "added" ? (
                              <span className="font-mono">+ v{c.toVersion}</span>
                            ) : (
                              <span className="font-mono">− v{c.fromVersion}</span>
                            )}
                          </span>
                          <Badge
                            variant="outline"
                            className="shrink-0 px-1 py-0 text-[10px]"
                          >
                            {KIND_STYLE[c.kind].label}
                          </Badge>
                        </button>

                        {c.changedFields.length > 0 && (
                          <p className="px-2 pb-1.5 pl-8 text-[11px] text-muted-foreground">
                            also changed: {c.changedFields.join(", ")}
                          </p>
                        )}

                        {isOpen && canExpand && (
                          <div className="border-t px-2">
                            <PromptTextDiff
                              // Remount on a different prompt/pair so the child
                              // starts from its "loading" initial state.
                              key={`${c.prompt_id}:${c.fromVersion}:${c.toVersion}`}
                              applicationId={to.application_id}
                              promptId={c.prompt_id}
                              fromVersion={c.fromVersion as number}
                              toVersion={c.toVersion as number}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Config + metadata */}
            {[
              { title: "Config", rows: diff.config },
              { title: "Metadata", rows: diff.metadata },
            ]
              .filter((s) => s.rows.length > 0)
              .map((section) => (
                <Card key={section.title} className="space-y-2 p-3">
                  <h4 className="font-medium">{section.title}</h4>
                  <div className="space-y-2">
                    {section.rows.map((r) => (
                      <div key={r.path} className="space-y-1">
                        <code className="font-mono text-xs text-muted-foreground">
                          {r.path}
                        </code>
                        <div className="grid gap-2 md:grid-cols-2">
                          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-destructive/10 p-2 text-xs">
                            {formatValue(r.from)}
                          </pre>
                          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-primary/10 p-2 text-xs">
                            {formatValue(r.to)}
                          </pre>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
          </div>
        )
      )}
    </div>
  );
};
